/**
 * Project CRUD — spec §3.3 projects/{projectId}
 * Core project operations against Firestore.
 */

import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  type Timestamp,
} from "firebase/firestore";
import { db } from "@/config/firebase";
import type { Project, ProjectSettings, StateMachineState, PricingTier } from "@/types";

const PROJECTS = "projects";

// ─── Create ────────────────────────────────────────────────────

export interface CreateProjectInput {
  title: string;
  author_name: string;
  author_id: string;
  settings: ProjectSettings;
  pricing_tier: PricingTier;
}

export async function createProject(input: CreateProjectInput): Promise<string> {
  const docRef = await addDoc(collection(db, PROJECTS), {
    ...input,
    status: "active",
    state: "CREATED" as StateMachineState,
    auto_advanced: false,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  });
  return docRef.id;
}

// ─── Read ──────────────────────────────────────────────────────

export async function getProject(projectId: string): Promise<Project | null> {
  const snap = await getDoc(doc(db, PROJECTS, projectId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as Project;
}

export async function getProjectsByUser(userId: string): Promise<Project[]> {
  const q = query(
    collection(db, PROJECTS),
    where("author_id", "==", userId),
    orderBy("created_at", "desc"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Project);
}

// ─── Update ────────────────────────────────────────────────────

export async function updateProject(
  projectId: string,
  updates: Partial<Pick<Project, "title" | "author_name" | "settings" | "pricing_tier" | "isbn">>,
): Promise<void> {
  await updateDoc(doc(db, PROJECTS, projectId), {
    ...updates,
    updated_at: serverTimestamp(),
  });
}

// ─── State Transition ──────────────────────────────────────────

import { VALID_TRANSITIONS } from "@/types";
import { addStateLogEntry } from "./stateLog";

export async function transitionState(
  projectId: string,
  toState: StateMachineState,
  trigger: string,
  metadata?: Record<string, unknown>,
): Promise<void> {
  const project = await getProject(projectId);
  if (!project) throw new Error(`Project ${projectId} not found`);

  const validTargets = VALID_TRANSITIONS[project.state];
  if (!validTargets.includes(toState)) {
    throw new Error(
      `Invalid state transition: ${project.state} → ${toState}. Valid targets: ${validTargets.join(", ")}`,
    );
  }

  // Update project state
  await updateDoc(doc(db, PROJECTS, projectId), {
    state: toState,
    status: toState === "ERROR" ? "error" : toState === "PUBLISHED" ? "completed" : "active",
    updated_at: serverTimestamp(),
    ...(toState === "ERROR" ? { error_message: trigger } : {}),
  });

  // Write audit log
  await addStateLogEntry(projectId, project.state, toState, trigger, metadata);
}

// ─── Delete ────────────────────────────────────────────────────

export async function deleteProject(projectId: string): Promise<void> {
  await deleteDoc(doc(db, PROJECTS, projectId));
}

// ─── Set Review Deadline ───────────────────────────────────────

export async function setReviewDeadline(
  projectId: string,
  deadline: Timestamp,
): Promise<void> {
  await updateDoc(doc(db, PROJECTS, projectId), {
    review_deadline: deadline,
    updated_at: serverTimestamp(),
  });
}
